/**
 * Stigmergy: the world remembers. Three scalar fields over the island,
 * deposited by behavior, decayed and diffused on a slow tick. Each is a
 * `local rule -> feedback -> macro pattern` machine:
 *   PATH  — walking deposits; steering follows the gradient -> desire-path
 *           networks appear where the throng actually travels.
 *   FOOD  — meals deposit; hungry creatures climb the gradient -> foraging
 *           columns toward your habitual feeding spots.
 *   JOY   — play deposits; play prefers joyful ground -> festival greens.
 */
import { PARAMS } from './params';

export const GRID_N = 96; // cells per axis, 1 world unit each
const HALF = PARAMS.WORLD_SIZE / 2;

export class Stigmergy {
  readonly path = new Float32Array(GRID_N * GRID_N);
  readonly food = new Float32Array(GRID_N * GRID_N);
  readonly joy = new Float32Array(GRID_N * GRID_N);
  private readonly scratch = new Float32Array(GRID_N * GRID_N);

  cellIndex(x: number, z: number): number {
    let cx = ((x + HALF) / PARAMS.WORLD_SIZE) * GRID_N;
    let cz = ((z + HALF) / PARAMS.WORLD_SIZE) * GRID_N;
    cx = cx < 0 ? 0 : cx >= GRID_N ? GRID_N - 1 : cx;
    cz = cz < 0 ? 0 : cz >= GRID_N ? GRID_N - 1 : cz;
    return (cz | 0) * GRID_N + (cx | 0);
  }

  deposit(field: Float32Array, x: number, z: number, amount: number, max = 4): void {
    const c = this.cellIndex(x, z);
    const v = (field[c] as number) + amount;
    field[c] = v > max ? max : v;
  }

  /**
   * Gradient of a field at (x, z) via central differences, written into out
   * as a unit-ish direction * magnitude. Returns magnitude.
   */
  gradient(field: Float32Array, x: number, z: number, out: { x: number; z: number }): number {
    const c = this.cellIndex(x, z);
    const cx = c % GRID_N;
    const cz = (c / GRID_N) | 0;
    const xm = field[cz * GRID_N + Math.max(0, cx - 1)] as number;
    const xp = field[cz * GRID_N + Math.min(GRID_N - 1, cx + 1)] as number;
    const zm = field[Math.max(0, cz - 1) * GRID_N + cx] as number;
    const zp = field[Math.min(GRID_N - 1, cz + 1) * GRID_N + cx] as number;
    out.x = (xp - xm) * 0.5;
    out.z = (zp - zm) * 0.5;
    return Math.abs(out.x) + Math.abs(out.z);
  }

  /** Slow tick (2 Hz): exponential decay everywhere, light diffusion on FOOD/JOY. */
  tickDecay(): void {
    decay(this.path, 0.997);
    decay(this.food, 0.985);
    decay(this.joy, 0.99);
    diffuse(this.food, this.scratch, 0.05);
    diffuse(this.joy, this.scratch, 0.08);
  }

  /**
   * Quantize PATH+JOY into a u8 grid for terrain rendering (R=wear, G=joy).
   * Thresholded so only genuinely busy routes read as worn earth — a cell
   * needs sustained traffic, not a single crossing.
   */
  writeRenderGrid(out: Uint8Array): void {
    for (let i = 0; i < GRID_N * GRID_N; i++) {
      const wear = smooth01(((this.path[i] as number) - 0.28) / 1.5);
      const joy = smooth01(((this.joy[i] as number) - 0.3) / 1.4);
      out[i * 2] = (wear * 255) | 0;
      out[i * 2 + 1] = (joy * 255) | 0;
    }
  }
}

function smooth01(t: number): number {
  const x = t < 0 ? 0 : t > 1 ? 1 : t;
  return x * x * (3 - 2 * x);
}

function decay(field: Float32Array, factor: number): void {
  for (let i = 0; i < field.length; i++) {
    const v = (field[i] as number) * factor;
    field[i] = v < 0.001 ? 0 : v;
  }
}

/** One Jacobi blur step: field += rate * (4-neighborhood mean - field). */
function diffuse(field: Float32Array, scratch: Float32Array, rate: number): void {
  scratch.set(field);
  for (let z = 1; z < GRID_N - 1; z++) {
    for (let x = 1; x < GRID_N - 1; x++) {
      const c = z * GRID_N + x;
      const mean =
        ((scratch[c - 1] as number) +
          (scratch[c + 1] as number) +
          (scratch[c - GRID_N] as number) +
          (scratch[c + GRID_N] as number)) *
        0.25;
      field[c] = (scratch[c] as number) + rate * (mean - (scratch[c] as number));
    }
  }
}
